import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Logo } from "../components/Logo";
import { useAuth } from "../context/AuthContext";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const next = new URLSearchParams(location.search).get("next") || "/dashboard";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const r = await login(email.trim(), password);
    setLoading(false);
    if (!r.ok) setError(r.error);
    else navigate(next);
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2" data-testid="login-page">
      <aside className="bg-[var(--es-blue)] text-white relative overflow-hidden hidden md:flex flex-col justify-between p-12">
        <Link to="/" className="inline-flex items-center gap-2 z-10">
          <Logo size={32} variant="mark" />
          <span className="font-display text-xl">Erudition <span className="italic">Score</span></span>
        </Link>
        <div className="z-10">
          <h1 className="font-display text-5xl leading-[1.05]">
            Welcome back. <br />
            <em className="text-white/80">Pick up where you left off.</em>
          </h1>
          <p className="font-body text-white/70 mt-6 max-w-md">
            Your assessment history, growth plan and progress chart are saved to your account.
          </p>
        </div>
        <div className="absolute -right-32 -bottom-32 opacity-10">
          <Logo size={620} variant="mark" />
        </div>
      </aside>

      <main className="flex items-center justify-center p-8 md:p-12">
        <form onSubmit={onSubmit} className="w-full max-w-md fade-in" data-testid="login-form">
          <p className="font-ui text-xs uppercase tracking-[0.28em] text-[var(--es-blue)]">Sign in</p>
          <h2 className="font-display text-4xl text-[var(--es-ink)] mt-2">Enter your account</h2>
          <p className="font-body text-[var(--es-ink-soft)] mt-2">
            New here?{" "}
            <Link to="/register" className="text-[var(--es-blue)] underline" data-testid="login-to-register">
              Create an account
            </Link>
            .
          </p>

          <div className="mt-10 space-y-6">
            <div>
              <label className="font-ui text-[11px] tracking-widest uppercase text-[var(--es-ink-soft)]">
                Email
              </label>
              <input
                type="email"
                value={email}
                required
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full border-b border-[var(--es-line)] focus:border-[var(--es-blue)] outline-none py-3 bg-transparent font-body"
                data-testid="login-email-input"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="font-ui text-[11px] tracking-widest uppercase text-[var(--es-ink-soft)]">
                Password
              </label>
              <input
                type="password"
                value={password}
                required
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full border-b border-[var(--es-line)] focus:border-[var(--es-blue)] outline-none py-3 bg-transparent font-body"
                data-testid="login-password-input"
                autoComplete="current-password"
              />
            </div>
          </div>

          {error && (
            <p className="mt-6 text-sm text-red-600 font-body" data-testid="login-error">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="mt-8 w-full bg-[var(--es-blue)] text-white py-4 hover:bg-[var(--es-blue-hover)] disabled:opacity-60 transition-colors uppercase tracking-wider text-sm font-semibold font-ui"
            data-testid="login-submit-button"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>

          <p className="mt-6 text-xs text-[var(--es-ink-soft)] font-ui">
            Test account: test@erudition.app · Test!2025
          </p>
        </form>
      </main>
    </div>
  );
}
